/**
 * Main-thread handle on the decode worker.
 *
 * LibRaw-Wasm spawns a worker of its own, so the normal path is a nested
 * worker. That works in current Chrome and Safari, but if a browser refuses it
 * the decode still has to happen, so a same-thread fallback is kept: slower and
 * it does block, but the user gets their photograph.
 */

import type { DecodeRequest, DecodeResponse, DecodedImage } from '@lumos/core';
import { decodeSource } from './decode.ts';

export interface DecodeHandle {
  promise: Promise<DecodedImage>;
  cancel: () => void;
}

type Pending = {
  resolve: (image: DecodedImage) => void;
  reject: (error: Error) => void;
  onProgress?: (stage: string, message: string) => void;
  cancelled: boolean;
};

let worker: Worker | null = null;
let workerUsable = true;
const pending = new Map<string, Pending>();
let nextId = 0;

function ensureWorker(): Worker | null {
  if (!workerUsable) return null;
  if (worker) return worker;
  try {
    worker = new Worker(new URL('./decoder.worker.ts', import.meta.url), { type: 'module' });
    worker.onmessage = (event: MessageEvent<DecodeResponse>) => {
      const message = event.data;
      const entry = pending.get(message.id);
      if (!entry) return;
      if (message.type === 'progress') {
        entry.onProgress?.(message.stage, message.message);
        return;
      }
      pending.delete(message.id);
      if (entry.cancelled) return;
      if (message.type === 'done') entry.resolve(message.image);
      else entry.reject(new Error(message.message));
    };
    worker.onerror = (event) => {
      // A hard worker failure is not recoverable per-request; fall back for
      // everything that follows rather than failing every future decode.
      workerUsable = false;
      worker?.terminate();
      worker = null;
      for (const [, entry] of pending) {
        entry.reject(new Error(event.message || 'The decode worker failed to start.'));
      }
      pending.clear();
    };
    return worker;
  } catch {
    workerUsable = false;
    return null;
  }
}

export function decodeFile(
  file: File | Blob,
  filename: string,
  options: { maxWorkingEdge: number; full?: boolean; onProgress?: (stage: string, message: string) => void },
): DecodeHandle {
  const id = `decode-${nextId++}`;
  let cancelled = false;

  const promise = (async () => {
    const buffer = await file.arrayBuffer();
    if (cancelled) throw new Error('cancelled');

    const active = ensureWorker();
    if (!active) {
      return decodeSource(buffer, {
        filename,
        maxWorkingEdge: options.maxWorkingEdge,
        full: options.full ?? false,
        onProgress: options.onProgress,
      });
    }

    return new Promise<DecodedImage>((resolve, reject) => {
      pending.set(id, {
        resolve,
        reject,
        onProgress: options.onProgress,
        get cancelled() {
          return cancelled;
        },
      } as Pending);

      const request: DecodeRequest = {
        id,
        buffer,
        filename,
        maxWorkingEdge: options.maxWorkingEdge,
        full: options.full ?? false,
      };
      active.postMessage(request, [buffer]);
    }).catch(async (error: Error) => {
      // The worker path failed for this file. If the worker itself died, retry
      // once on the main thread before giving up on the photograph.
      if (!workerUsable && !cancelled) {
        return decodeSource(await file.arrayBuffer(), {
          filename,
          maxWorkingEdge: options.maxWorkingEdge,
          full: options.full ?? false,
          onProgress: options.onProgress,
        });
      }
      throw error;
    });
  })();

  return {
    promise,
    cancel: () => {
      cancelled = true;
      const entry = pending.get(id);
      if (entry) entry.cancelled = true;
    },
  };
}
